import React, { useContext, useMemo, useState } from 'react';
import proptypes from 'prop-types';
import { Pagination, PaginationCompact, PaginationItem } from '@one-thd/sui-atomic-components';
import {
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { triggerAnalytics } from '../../Analytics';
import QAContext from '../../context/QAContext';
import {
  DEFAULT_NUMBER_OF_PAGES,
  QUESTION_DISPLAY_PER_PAGE,
  TOTAL_RESULTS_LIMIT,
} from '../../constants';
import './Pager.scss';

const { number } = proptypes;

export const Pager = ({ seoTotalResults, seoPageNumber }) => {
  const {
    currentPage, dispatch, TotalResults, itemId, channel
  } = useContext(QAContext);

  const isMobile = channel === 'mobile';

  const results = TotalResults || seoTotalResults;
  const { data } = useDataModel('product', { variables: { itemId } });
  const seoLink = data?.product?.identifiers?.canonicalUrl?.replace('/p', '/p/questions');
  const [pagerNumber, setPagerNumber] = useState(currentPage);

  const totalPages = useMemo(() => {
    if (!results) return DEFAULT_NUMBER_OF_PAGES;
    if (results >= TOTAL_RESULTS_LIMIT) return Math.floor(TOTAL_RESULTS_LIMIT / QUESTION_DISPLAY_PER_PAGE);
    return Math.ceil(results / QUESTION_DISPLAY_PER_PAGE);
  }, [results]);

  const scrollToQASection = () => {
    const el = document.getElementById('questions_answers_accordion_title');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleChange = (event, value) => {
    event.preventDefault();
    setPagerNumber(value);

    dispatch({ action: 'SET_CURRENT_PAGE', value });

    const newStartIndex = value === DEFAULT_NUMBER_OF_PAGES
      ? DEFAULT_NUMBER_OF_PAGES
      : QUESTION_DISPLAY_PER_PAGE * (value - DEFAULT_NUMBER_OF_PAGES) + DEFAULT_NUMBER_OF_PAGES;

    dispatch({ action: 'SET_START_INDEX', value: newStartIndex });

    const payload = { component: 'pagination' };
    triggerAnalytics(payload);
    scrollToQASection();
  };
  // indexes first page as 0
  // const indexablePages = totalPages > 6 ? 6 : totalPages;
  const indexablePages = Math.min(totalPages, 6);

  const renderItem = (item) => {
    if (item.page > indexablePages) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <PaginationItem {...item} />;
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <PaginationItem {...item} href={item.page ? `${seoLink}/${item.page}` : null} />;
  };

  const pageNumberIncludingJsOff = typeof window === 'undefined' ? seoPageNumber : pagerNumber;

  return (
    isMobile ? (
      <>
        <PaginationCompact
          renderItem={renderItem}
          count={totalPages}
          page={pageNumberIncludingJsOff}
          onChange={handleChange}
          fullWidth
          data-testid="pagination-compact"
          disable={totalPages === 1}
        />
      </>
    ) : (
      <>
        <Pagination
          renderItem={renderItem}
          count={totalPages}
          page={pageNumberIncludingJsOff}
          onChange={handleChange}
          fullWidth
          data-testid="pagination"
          disable={totalPages === 1}
        />
      </>
    )
  );
};

Pager.displayName = 'Pager';

Pager.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    identifiers: shapeType({
      canonicalUrl: stringType(),
    }),
  }),
};

Pager.propTypes = {
  seoTotalResults: number,
  seoPageNumber: number
};

Pager.defaultProps = {
  seoTotalResults: null,
  seoPageNumber: null,
};
